import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { format, formatDistanceToNow } from 'date-fns'
import { KeyRound, Plus, Bot, ShieldCheck, Plug, BadgeCheck, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageHeader, EmptyState } from '@/components/shared'
import { apiTokenService } from '@/services/apiTokenService'
import CreateTokenDialog from '@/components/ApiTokens/CreateTokenDialog'
import TokenRevealDialog from '@/components/ApiTokens/TokenRevealDialog'
import ConnectAiToolsPanel from '@/components/ApiTokens/ConnectAiToolsPanel'
import AdminTokensPanel from '@/components/ApiTokens/AdminTokensPanel'
import AiAccessPolicyPanel from '@/components/ApiTokens/AiAccessPolicyPanel'
import { useAuthStore } from '@/store/authStore'

const STATUS_VARIANT = { active: 'success', expired: 'secondary', revoked: 'destructive' }

const relative = (date) => (date ? formatDistanceToNow(new Date(date), { addSuffix: true }) : 'never')

const ApiTokens = () => {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [createOpen, setCreateOpen] = useState(false)
  const [revealed, setRevealed] = useState(null) // { token, apiToken }
  const [toRevoke, setToRevoke] = useState(null)

  const { data: metaData } = useQuery('api-token-meta', apiTokenService.getMeta)
  const { data: tokensData, isLoading } = useQuery('api-tokens', apiTokenService.list)
  const meta = metaData?.data
  const allTokens = tokensData?.data?.tokens || []
  const tokens = allTokens.filter(t => t.kind !== 'oauth')
  const apps = allTokens.filter(t => t.kind === 'oauth')
  const policy = meta?.policy
  const canCreate = !!meta && policy?.pat_enabled !== false && policy?.role_allowed !== false
  const policyNotice = !policy ? null
    : !meta.mcp_enabled ? 'AI tools are turned off by your administrator.'
    : !policy.role_allowed ? "Your administrator hasn't allowed AI tools for your role."
    : null

  const handleRevoke = async () => {
    try {
      await apiTokenService.revoke(toRevoke.id)
      queryClient.invalidateQueries('api-tokens')
      queryClient.invalidateQueries('admin-api-tokens')
      toast.success(toRevoke.kind === 'oauth' ? `${toRevoke.name} disconnected` : `Token "${toRevoke.name}" revoked`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke token')
    } finally {
      setToRevoke(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="API tokens & AI tools"
        description="Connect Claude Code, Cursor or other AI tools to Project Central so they can read your tickets (with screenshots and documents), comment, move status and track time for you."
        actions={
          <Button size="sm" className="h-8 px-3 text-sm" onClick={() => setCreateOpen(true)} disabled={!canCreate}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Create token
          </Button>
        }
      />

      {policyNotice && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{policyNotice}</span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Bot className="h-5 w-5" /> Connect an AI tool</CardTitle>
          <CardDescription>
            Add the server to your tool and sign in when it opens Buzzinga in the browser: no token to copy.
            Then ask your assistant to work on a ticket by its key, e.g. "PC-42".
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ConnectAiToolsPanel mcpUrl={meta?.mcp_url} apiUrl={meta?.api_url} connectionDays={policy?.oauth_connection_days} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Plug className="h-5 w-5" /> Connected apps</CardTitle>
          <CardDescription>Apps you connected by signing in. Disconnect one and it stops working at once.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : apps.length === 0 ? (
            <p className="text-sm text-muted-foreground">No connected apps yet. Connect Claude Code or another tool with the steps above.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>App</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Sign in again</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map(a => (
                    <TableRow key={a.id} className={a.status !== 'active' ? 'opacity-60' : undefined}>
                      <TableCell className="font-medium">
                        {a.name}
                        <div className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
                          {a.app?.verified_host && <BadgeCheck className="h-3 w-3 text-green-600" />}
                          {a.app?.host || 'unknown app'} · connected {format(new Date(a.created_at), 'MMM d, yyyy')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {a.scopes.map(s => <Badge key={s} variant="outline" className="text-[10px] font-normal">{s}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{relative(a.last_used_at)}</TableCell>
                      <TableCell className="text-sm">{format(new Date(a.expires_at), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[a.status]}>{a.status === 'revoked' ? 'disconnected' : a.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {a.status === 'active' && (
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setToRevoke(a)}>
                            Disconnect
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><KeyRound className="h-5 w-5" /> Your tokens</CardTitle>
          <CardDescription className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            Tokens only reach Project Central (never employee records or admin pages), expire automatically, and every use is logged.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : tokens.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="No tokens yet"
              description="Most tools can connect by signing in instead. Create a token for scripts, CI or tools that can't."
              actionLabel={canCreate ? 'Create token' : undefined}
              onAction={canCreate ? () => setCreateOpen(true) : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead>Permissions</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokens.map(t => {
                      const expiresSoon = t.status === 'active' && new Date(t.expires_at) - Date.now() < 7 * 24 * 3600 * 1000
                      return (
                        <TableRow key={t.id} className={t.status !== 'active' ? 'opacity-60' : undefined}>
                          <TableCell className="font-medium">
                            {t.name}
                            <div className="text-xs text-muted-foreground">created {format(new Date(t.created_at), 'MMM d, yyyy')}</div>
                          </TableCell>
                          <TableCell><code className="text-xs">{t.token_prefix}…</code></TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {t.scopes.map(s => <Badge key={s} variant="outline" className="text-[10px] font-normal">{s}</Badge>)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {t.last_used_at ? (
                              <Tooltip>
                                <TooltipTrigger className="underline decoration-dotted">{relative(t.last_used_at)}</TooltipTrigger>
                                <TooltipContent>
                                  {t.last_used_ip || 'unknown IP'}{t.last_used_user_agent ? ` · ${t.last_used_user_agent}` : ''}
                                </TooltipContent>
                              </Tooltip>
                            ) : 'never'}
                          </TableCell>
                          <TableCell className={expiresSoon ? 'text-sm text-amber-600 font-medium' : 'text-sm'}>
                            {format(new Date(t.expires_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {t.status === 'active' && (
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setToRevoke(t)}>
                                Revoke
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </div>
          )}
        </CardContent>
      </Card>

      {user?.role === 'admin' && <AiAccessPolicyPanel />}
      {user?.role === 'admin' && <AdminTokensPanel />}

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        meta={meta}
        onCreated={(token, apiToken) => {
          queryClient.invalidateQueries('api-tokens')
          setRevealed({ token, apiToken })
        }}
      />

      <TokenRevealDialog
        token={revealed?.token}
        apiToken={revealed?.apiToken}
        meta={meta}
        onClose={() => setRevealed(null)}
      />

      <AlertDialog open={!!toRevoke} onOpenChange={(open) => !open && setToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{toRevoke?.kind === 'oauth' ? `Disconnect ${toRevoke?.name}?` : `Revoke "${toRevoke?.name}"?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {toRevoke?.kind === 'oauth'
                ? 'The app loses access immediately. To use it again, connect it again and approve.'
                : 'Any AI tool or script using this token stops working immediately. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {toRevoke?.kind === 'oauth' ? 'Disconnect' : 'Revoke token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ApiTokens
